import { useMemo } from "react";

import useSettings from "../../data/useSettings";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import usePartHistoryEnabled from "../../hooks/usePartHistoryEnabled";
import {
  groupSettingsByPrefix,
  settingDisplayKey,
  settingTabHref,
  settingTabLabel,
} from "../AdminCredentials/settingTabs";
import { ADMIN_GROUPS, AdminPageGate, IAdminGroup, SETTINGS_GROUP_KEY } from "./adminSections";

export type AdminLinkKind = "page" | "settingsTab" | "setting";

export interface IAdminLink {
  key: string;
  kind: AdminLinkKind;
  label: string;
  description: string;
  /** Absolute path, so it works from the landing page and from inside a section. */
  href: string;
  /** The `:section` this link lands on, used to work out which pill is current. */
  section: string;
  /** The Admin Settings tab this link lands on, for settingsTab and setting links. */
  tab?: string;
  keywords: string[];
}

export interface IAdminSectionGroup extends Omit<IAdminGroup, "pages"> {
  /** What the card shows: the group's pages, or on the Settings card the customer's settings tabs. */
  links: IAdminLink[];
  /** Individual settings. Only surfaced by the search, each landing on its tab. */
  settings: IAdminLink[];
}

/**
 * The admin groups this customer can actually see: feature-gated pages dropped, and the
 * prefixes from their Admin Settings merged in as pills on the right card.
 */
export default function useAdminSections() {
  const hasTriage = CheckFeatureFlag("Triage");
  const hasDefra = CheckFeatureFlag("DEFRA");
  // Exact Betty flag spelling - matches PartProTradeService.FeatureName in the API and the help article gate
  const hasPartProTrade = CheckFeatureFlag("PartPro Trade");
  const hasTill = CheckFeatureFlag("Till");
  const partHistoryEnabled = usePartHistoryEnabled();
  const { generalSettings, loading } = useSettings();

  const { tabs, grouped } = useMemo(() => groupSettingsByPrefix(generalSettings), [generalSettings]);

  const groups = useMemo<IAdminSectionGroup[]>(() => {
    const gates: Record<AdminPageGate, boolean> = {
      Triage: hasTriage,
      DEFRA: hasDefra,
      "PartPro Trade": hasPartProTrade,
      Till: hasTill,
      "Part History": partHistoryEnabled,
    };

    return ADMIN_GROUPS.map((group) => {
      const pages: IAdminLink[] = group.pages
        .filter((page) => !page.gate || gates[page.gate])
        .map((page): IAdminLink => ({
          key: `page:${page.href}`,
          kind: "page",
          label: page.label,
          description: page.description,
          href: `/admin/${page.href}`,
          section: page.href,
          keywords: page.keywords ?? [],
        }));

      // Every prefix sits on the Settings card; General is already first in `tabs`
      const groupTabs = group.key === SETTINGS_GROUP_KEY ? tabs : [];

      const tabLinks: IAdminLink[] = groupTabs.map((tab): IAdminLink => {
        const rows = grouped[tab] ?? [];
        return {
          key: `tab:${tab}`,
          kind: "settingsTab",
          label: settingTabLabel(tab),
          description: `${rows.length} ${rows.length === 1 ? "setting" : "settings"}`,
          href: settingTabHref(tab),
          section: "admin-credentials",
          tab,
          keywords: rows.map((row) => settingDisplayKey(row.key)),
        };
      });

      const settings: IAdminLink[] = groupTabs.flatMap((tab) =>
        (grouped[tab] ?? []).map((row): IAdminLink => ({
          key: `setting:${row.key}`,
          kind: "setting",
          label: settingDisplayKey(row.key),
          description: `${settingTabLabel(tab)} setting`,
          href: settingTabHref(tab),
          section: "admin-credentials",
          tab,
          keywords: [row.key],
        }))
      );

      return {
        key: group.key,
        name: group.name,
        description: group.description,
        icon: group.icon,
        links: [...pages, ...tabLinks],
        settings,
      };
    });
  }, [tabs, grouped, hasTriage, hasDefra, hasPartProTrade, hasTill, partHistoryEnabled]);

  return { groups, tabs, loading };
}

/** The group and link a section (and optional Admin Settings tab) belongs to, for the sub-page strip. */
export function findAdminLink(groups: IAdminSectionGroup[], section: string, tab: string | null) {
  for (const group of groups) {
    const link = group.links.find((candidate) =>
      candidate.section === section && (candidate.kind !== "settingsTab" || !tab || candidate.tab === tab)
    );
    if (link) return { group, link };
  }
  return undefined;
}
